<script lang="ts">
    import { Component, mixins, Prop } from "vue-facing-decorator";
    import { BreakpointHandler, TicketSelectionDisplay } from "@/mixins";
    import Panel from "@/components/molecules/panel/Panel.vue";
    import { IPanelState } from "@/components/molecules";
    import { FeeBaseType, MathOperationType, StringHelper, TranslationKey } from "webshop-modules";
    import { EventType, FeeList, TicketSelectionApplicationData } from "@/domain/ticket-selection";

    @Component({
        components: {Panel}
    })
    export default class EventInformationComponent extends mixins(BreakpointHandler, TicketSelectionDisplay) {
        @Prop() readonly data!: TicketSelectionApplicationData;

        public readonly eventType = EventType
        public readonly Translation: typeof TranslationKey = TranslationKey;

        public accessibilityInformationState: IPanelState = {
            title: 'Information for people with disabilities',
            isClosable: true,
            isContentShown: false
        };

        public additionalInformationState: IPanelState = {
            title: 'Additional information',
            isClosable: true,
            isContentShown: true
        };

        public importantInformationState: IPanelState = {
            title: 'Important information',
            isClosable: false,
            isContentShown: true
        };

        public baseTicketFeeDescription = '';

        public get canDisplayFeeInformation(): boolean {
            return !!this.data.feeList && this.data.feeList.length > 0;
        }

        public ticketFeeFormattedValues(): string {
            if (!this.data.feeList) {
                return '';
            }

            const formattedValues = this.data.feeList.map(fee => this.getFormattedValueFor(fee));

            return formattedValues.join(' + ');
        }

        public composeTicketFeeDescriptionFrom(fees: FeeList[], baseTranslation: string) {
            let formattedTranslation = baseTranslation;

            const hasFixedFees = fees.some(fee => fee.baseType === FeeBaseType.TicketAmount);
            const hasPercentageFees = fees.some(fee => fee.baseType === FeeBaseType.TicketPriceSum);

            if (fees.length === 0 || (!hasFixedFees && !hasPercentageFees)) {
                return '';
            }

            if (hasFixedFees) {
                const allFixedFees = fees.filter(fee => fee.baseType === FeeBaseType.TicketAmount);

                formattedTranslation = StringHelper.replaceContentFrom(
                    formattedTranslation,
                    '{fixedValue}',
                    '{/fixedValue}',
                    allFixedFees.map(fee => `${fee.value} ${fee.currency}`).join(' + ')
                );

                formattedTranslation = StringHelper.removePatternFromString(
                    formattedTranslation,
                    ['{fixed}', '{/fixed}']
                );
            } else {
                formattedTranslation = StringHelper.replaceContentFrom(
                    formattedTranslation,
                    '{fixed}',
                    '{/fixed}',
                    ''
                );
            }

            if (hasPercentageFees) {
                const allPercentageFees = fees.filter(fee => fee.baseType === FeeBaseType.TicketPriceSum);

                formattedTranslation = StringHelper.replaceContentFrom(
                    formattedTranslation,
                    '{percentageValue}',
                    '{/percentageValue}',
                    allPercentageFees.map(fee => `${(fee.value * 100).toFixed(1)}%`).join(' + ')
                );

                formattedTranslation = StringHelper.removePatternFromString(
                    formattedTranslation,
                    ['{percentage}', '{/percentage}']
                );
            } else {
                formattedTranslation = StringHelper.replaceContentFrom(
                    formattedTranslation,
                    '{percentage}',
                    '{/percentage}',
                    ''
                );


            }

            if (hasFixedFees && hasPercentageFees) {
                formattedTranslation = StringHelper.removePatternFromString(
                    formattedTranslation,
                    ['{separator}', '{/separator}']
                );
            } else {
                formattedTranslation = StringHelper.replaceContentFrom(
                    formattedTranslation,
                    '{separator}',
                    '{/separator}',
                    ''
                );
            }

            return formattedTranslation;
        }

        /** Update important information state */
        protected updateAccessibilityInformationState(state: IPanelState): void {
            this.accessibilityInformationState = state;
        }

        /** Update important information state */
        protected updateAdditionalInformationState(state: IPanelState): void {
            this.additionalInformationState = state;
        }

        /** Update important information state */
        protected updateImportantInformationState(state: IPanelState): void {
            this.importantInformationState = state;
        }

        private async setComponentTranslations() {
            this.accessibilityInformationState.title = await this.$translationService.getTranslationFor(TranslationKey.Accessibility_title);
            this.additionalInformationState.title = await this.$translationService.getTranslationFor(TranslationKey.Additional_event_information);
            this.importantInformationState.title = await this.$translationService.getTranslationFor(TranslationKey.Important_information);
            this.baseTicketFeeDescription = await this.$translationService.getTranslationFor(TranslationKey.Ticket_fee_description);
        }

        private created() {
            if (!this.data) {
                return;
            }

            this.setComponentTranslations();
        }

        private getFormattedValueFor(fee: FeeList) {
            const isPercentageFee = fee.baseType === FeeBaseType.TicketPriceSum
                && fee.mathOperationType === MathOperationType.Multiply;

            if (isPercentageFee) {
                return `${(fee.value * 100).toFixed(1)}%`;
            }

            return `${fee.value} ${fee.currency}`;
        }
    }
</script>

<template>
    <Panel :state="importantInformationState" class="p-ticketSelection__additionalInfo"
           v-show="!displayStateStore.isFullScreenSeatmap(data)">
        <template v-if="canDisplayFeeInformation">
            <div class="m-panel__row">
                <div class="p-ticketSelection__feePitch">
                    *&nbsp;
                    <h4 v-translate="{
                     key: Translation.Ticket_fee_pitch,
                     keyValues: JSON.stringify({ 'placeholder-fee-values': ticketFeeFormattedValues() }),
                     identifierStart: '{var}',
                     identifierEnd: '{/var}',
                     }">
                    </h4>
                </div>
                <p v-html="composeTicketFeeDescriptionFrom(data.feeList ?? [], baseTicketFeeDescription)"></p>
                <p v-for="ticketFee in data.feeList" :key="ticketFee.value" v-html="ticketFee.description">
                </p>
            </div>
        </template>

        <template v-if="data.configuration.reCaptcha.key">
            <div class="p-ticketSelection__recaptcha m-panel__row"
                 v-translate="{ key: Translation.Recaptcha_terms_conditions }">
                This site is protected by reCAPTCHA and the Google
                <a href="https://policies.google.com/privacy">Privacy Policy</a> and
                <a href="https://policies.google.com/terms">Terms of Service</a> apply.
            </div>
        </template>

        <div v-if="data.additionalInformation.performanceDescription"
             v-html="data.additionalInformation.performanceDescription"
             class="m-panel__row a-editedContent"
        ></div>

        <div class="m-panel__row">
            <template v-if="data.eventInformation.eventType === eventType.Voucher">
                <p v-translate="{ key: Translation.Reservation_notice_voucher }"></p>
            </template>

            <template v-else>
                <p v-translate="{
                            key: Translation.Reservation_notice_common,
                            keyValues: JSON.stringify({ max_tickets: data.eventInformation.maxTickets })
                        }"></p>
            </template>
        </div>
    </Panel>

    <Panel class="p-ticketSelection__additionalInfo"
           :state="additionalInformationState"
           v-if="displayStateStore.hasAdditionalInformation"
           v-show="!displayStateStore.isFullScreenSeatmap(data)"
           v-on:toggle="updateAdditionalInformationState">
        <div
            v-if="data.additionalInformation.promoter && data.additionalInformation.promoter.name && data.additionalInformation.promoter.name !== ''"
            class="m-panel__row">
            <h3 v-translate="{ key: Translation.Performance_promoter_information }"></h3>
            <p>{{ data.additionalInformation.promoter.name }}</p>
            <p v-if="data.additionalInformation.promoter.street
                          || data.additionalInformation.promoter.postalNumber
                          || data.additionalInformation.promoter.city
                          || data.additionalInformation.promoter.country">
                    <span v-if="data.additionalInformation.promoter.street">{{
                            data.additionalInformation.promoter.street
                        }}</span><br/>
                <span v-if="data.additionalInformation.promoter.postalNumber">{{
                        data.additionalInformation.promoter.postalNumber
                    }}</span><br/>
                <span v-if="data.additionalInformation.promoter.city">{{
                        data.additionalInformation.promoter.city
                    }}</span><br/>
                <span v-if="data.additionalInformation.promoter.country">{{
                        data.additionalInformation.promoter.country.translatedName
                    }}</span>
            </p>
        </div>

        <div v-if="data.additionalInformation.fanTicket.isAvailable" class="m-panel__row">
            <img :src="data.additionalInformation.fanTicket.imgUrl" :alt="'Fan Ticket'"/>
            <div v-translate="{ key: Translation.Fan_ticket_information }">FanTicket information</div>
        </div>
    </Panel>

    <Panel class="p-ticketSelection__additionalInfo"
           :state="accessibilityInformationState"
           v-show="!displayStateStore.isFullScreenSeatmap(data)"
           v-on:toggle="updateAccessibilityInformationState"
           v-if="data.additionalInformation.accessibilityInformation
                               && data.additionalInformation.accessibilityInformation !== ''
                               && data.eventInformation.eventType !== eventType.Voucher">
        <div class="m-panel__row">
            <p>
                <a class="text-link"
                   :href="data.additionalInformation.accessibilityInformation"
                   v-translate="{ key: Translation.Accessibility_more_information }"
                   rel="external"></a>
            </p>
        </div>
    </Panel>
</template>
